import { Flex, Text } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import { AIFieldLabel } from "../../../../Common/AIFieldLabel";
import { useAIPopulatedFields } from "../../../../Common/hooks/useAIPopulatedFields";
import { FormValues } from "../../OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm/types";
import { AddressFieldNames } from "./constants";
import CountryFormField from "./CountryFormField";
import StateFormField from "./StateFormField";

type AddressFieldsProps = {
  editMode?: boolean;
  aiExtractionSupported?: boolean;
  /** Onboarding layout: country first, then streets, city, state|zip (Figma Initial-6+). */
  variant?: "default" | "onboarding";
};

const AddressFields = ({
  editMode = false,
  aiExtractionSupported = false,
  variant = "default",
}: AddressFieldsProps) => {
  const { hasFieldPopulatedByAI } = useAIPopulatedFields<FormValues>({
    editMode,
    fieldNames: Object.values(AddressFieldNames),
    aiExtractionSupported,
  });

  if (variant === "onboarding") {
    return (
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Text variant="strong-md">Domicile address</Text>
        <CountryFormField
          populatedByAI={hasFieldPopulatedByAI(AddressFieldNames.Country)}
          placeholder="Select a country"
          full
          hideLabel
        />
        <FormTextInput
          label={
            <AIFieldLabel
              label="Street (line 1)"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.StreetLine1)}
            />
          }
          name={AddressFieldNames.StreetLine1}
          data-testid="street-line1"
          isFullWidth
        />
        <FormTextInput
          label={
            <AIFieldLabel
              label="Street (line 2)"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.StreetLine2)}
            />
          }
          name={AddressFieldNames.StreetLine2}
          data-testid="street-line2"
          isFullWidth
        />
        <FormTextInput
          label={
            <AIFieldLabel
              label="City"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.City)}
            />
          }
          name={AddressFieldNames.City}
          data-testid="city"
          isFullWidth
        />
        <Flex justifyContent="flex-start" gap={12} style={{ marginBottom: SPACING.MEDIUM }}>
          <StateFormField
            isFull
            populatedByAI={hasFieldPopulatedByAI(AddressFieldNames.StateOrProvince)}
          />
          <FormTextInput
            label={
              <AIFieldLabel
                label="Zip/Postal Code"
                showIcon={hasFieldPopulatedByAI(AddressFieldNames.ZipCode)}
              />
            }
            name={AddressFieldNames.ZipCode}
            data-testid="zipCode"
            isFullWidth
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <Text variant="strong-md" marginBottom="md">
        Domicile address
      </Text>
      <div style={{ marginBottom: SPACING.MEDIUM }}>
        <FormTextInput
          label={
            <AIFieldLabel
              label="Street (line 1)"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.StreetLine1)}
            />
          }
          name={AddressFieldNames.StreetLine1}
          data-testid="street-line1"
          isFullWidth
        />
      </div>
      <div style={{ marginBottom: SPACING.MEDIUM }}>
        <FormTextInput
          label={
            <AIFieldLabel
              label="Street (line 2)"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.StreetLine2)}
            />
          }
          name={AddressFieldNames.StreetLine2}
          data-testid="street-line2"
          isFullWidth
        />
      </div>
      <Flex justifyContent="flex-start" gap={12} style={{ marginBottom: SPACING.MEDIUM }}>
        <FormTextInput
          label={
            <AIFieldLabel
              label="City"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.City)}
            />
          }
          name={AddressFieldNames.City}
          data-testid="city"
        />
        <StateFormField
          populatedByAI={hasFieldPopulatedByAI(AddressFieldNames.StateOrProvince)}
        />
      </Flex>
      <Flex justifyContent="flex-start" gap={12} style={{ marginBottom: SPACING.MEDIUM }}>
        <FormTextInput
          label={
            <AIFieldLabel
              label="Zipcode"
              showIcon={hasFieldPopulatedByAI(AddressFieldNames.ZipCode)}
            />
          }
          name={AddressFieldNames.ZipCode}
          data-testid="zipCode"
        />
        <CountryFormField populatedByAI={hasFieldPopulatedByAI(AddressFieldNames.Country)} />
      </Flex>
    </>
  );
};

export default AddressFields;
